<template>
    <vx-table-row>
        <vx-table-row-item>
            <template v-slot:label>Changed at:</template>
            {{ shortDateTime(props.modelValue.created_at) }}
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label>Field:</template>
            <div class="flex flex-col">
                <span>{{ fieldLabels[props.modelValue.field] }}</span>
                <span class="text-xs text-slate-500">{{ sourceLabels[props.modelValue.source] }}</span>
            </div>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label>Change:</template>
            <div class="flex items-center gap-2 break-all">
                <span :class="{ 'text-slate-500': props.modelValue.old_value === null }" data-test="old-value">{{
                    props.modelValue.old_value ?? 'unset'
                }}</span>
                <font-awesome-icon widthAuto :icon="faArrowRight" class="text-slate-400"></font-awesome-icon>
                <span :class="{ 'text-slate-500': props.modelValue.new_value === null }" data-test="new-value">{{
                    props.modelValue.new_value ?? 'unset'
                }}</span>
            </div>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label>Changed by:</template>
            <div v-if="props.modelValue.user" class="flex flex-col">
                <span>{{ props.modelValue.user.name }}</span>
                <span class="text-xs text-slate-500">
                    {{ props.modelValue.user.email }}
                </span>
                <span v-if="props.modelValue.impersonator" class="text-xs text-slate-500" data-test="impersonator">
                    impersonated by {{ props.modelValue.impersonator.name }}
                </span>
            </div>
            <span v-else>Console</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label>Requested by:</template>
            <span v-if="props.modelValue.requested_by" class="break-all" data-test="requested-by">
                {{ props.modelValue.requested_by }}
            </span>
            <span v-else class="text-slate-500" data-test="requested-by">-</span>
        </vx-table-row-item>
    </vx-table-row>
</template>

<script setup lang="ts">
import { VxTableRow, VxTableRowItem } from '@voxie/frontend-components';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { shortDateTime } from '~/utils/date';

type PhoneNumberVoiceChangeSource =
    | 'admin_api'
    | 'switch_to_bandwidth'
    | 'complete_port'
    | 'correct_hosting'
    | 'complete_migration'
    | 'move_to_team'
    | 'team_migration'
    | 'provision_short_code';

type PhoneNumberVoiceChangeField =
    | 'team_id'
    | 'vendor'
    | 'is_hosted'
    | 'inbound_call_handler'
    | 'forward_voice_to'
    | 'forward_voice_send_digits'
    | 'voice_message_text';

interface ChangeActor {
    id: number;
    name: string;
    email: string;
}

export interface PhoneNumberVoiceChangeEntry {
    id: number;
    team_id: number;
    phone_number_id: number;
    phone_number: string;
    source: PhoneNumberVoiceChangeSource;
    field: PhoneNumberVoiceChangeField;
    old_value: string | null;
    new_value: string | null;
    requested_by: string | null;
    user: ChangeActor | null;
    impersonator: ChangeActor | null;
    created_at: string;
}

const fieldLabels: Record<PhoneNumberVoiceChangeField, string> = {
    team_id: 'Team',
    vendor: 'Vendor',
    is_hosted: 'Hosted',
    inbound_call_handler: 'Call handler',
    forward_voice_to: 'Forward to',
    forward_voice_send_digits: 'Digits after forward',
    voice_message_text: 'Message',
};

const sourceLabels: Record<PhoneNumberVoiceChangeSource, string> = {
    admin_api: 'Phone number settings',
    switch_to_bandwidth: 'Switch to Bandwidth',
    complete_port: 'Port completed',
    correct_hosting: 'Hosting corrected',
    complete_migration: 'Short code migration',
    move_to_team: 'Moved to team',
    team_migration: 'Team migration',
    provision_short_code: 'Short code provisioned',
};

const props = defineProps<{
    modelValue: PhoneNumberVoiceChangeEntry;
}>();
</script>
